import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import CMotivator from './CMotivator';
import { useCallback, useEffect, useMemo, useRef } from "react";
import Helpers from "@/Helpers";

export default function CMotivators(props) {
   const { sectionData, values, setValue } = props;
   const currentSection = sectionData?.currentSection;
   const content: Record<string, string> = Helpers.getSectionContent(currentSection, sectionData?.lang);
   const elements = Helpers.getSectionElements(content);
   const contentValues = useMemo(() => Array.isArray(elements?.items) ? elements.items : [], [elements]);
   const instructionsHtml = Helpers.getSectionHtmlValue(content, "instructions");
   const reminderHtml = Helpers.getSectionHtmlValue(content, "reminder");
   const headers = Array.isArray(elements?.headers) ? elements.headers : [];
   const containerRef = useRef(null);

   function isOnPage(item, page) {
      return Number(item?.page) === Number(page);
   }

   const onChange = useCallback(function (childId, value) {
      setValue(childId, parseInt(value));
   }, [setValue]);

   const getIssueCountForPage = useCallback(function (validationValues, page) {
      if (!Array.isArray(contentValues)) {
         return 0; // Return 0 if values array doesn't exist
      }
      const pageValues = contentValues.filter((m) => isOnPage(m, page));
      const count = pageValues.filter((m) => {
         const answer = validationValues[currentSection.formId + m.id];
         return answer === undefined || answer === null || answer === "" || Number(answer) === 0;
      }).length;
      return count;
   }, [contentValues, currentSection?.formId]);

   useEffect(() => {
      sectionData.registerSection( 767);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentSection?.name]);

   // Add keyboard input functionality
   useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      if (!sectionData?.isAdmin) return;

      const handleKeyDown = (e) => {
         if (sectionData?.isAdmin) {
            // Handle number keys 1-4 for radio button selection
            if (e.key >= '1' && e.key <= '4') {
               e.preventDefault();
               const keyValue = parseInt(e.key);

               // Find the first unfilled motivator on current page
                const page = Number(sectionData?.page || 1);
               const pageMotivators = contentValues.filter((m) => isOnPage(m, page));

               for (const motivator of pageMotivators) {
                  const fieldId = currentSection.formId + motivator.id;
                  const currentValue = values[fieldId];

                  if (!currentValue || currentValue === 0) {
                     // Found first unfilled motivator, set its value
                     void onChange(motivator.id, keyValue);
                     return;
                  }
               }
            }
            // Handle Enter key for next page (but not past the last page)
            else if (e.key === 'Enter') {
               e.preventDefault();
               const currentPage = Number(sectionData?.page || 1);
               const totalPages = contentValues.length > 0
                  ? Math.max(...contentValues.map(m => Number(m?.page || 1)))
                  : 1;
               if (currentPage < totalPages && sectionData.onNextPage)
                  sectionData.onNextPage();
            }
         }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
         document.removeEventListener('keydown', handleKeyDown);
      };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentSection?.formId, sectionData?.page, contentValues, values, onChange, sectionData?.isAdmin]);

   useEffect(() => {
      const nextCount = Number(sectionData?.nextCount || 0);
      const page = Number(sectionData?.page || 1);
      const issueCount = nextCount > 0 ? getIssueCountForPage(values || {}, page) : 0;
      sectionData?.updateIssueCount?.(issueCount);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [contentValues, currentSection?.formId, getIssueCountForPage, sectionData?.nextCount, sectionData?.page, sectionData?.updateIssueCount, values]);

   const page = Number(sectionData?.page || 1);
   return (
      <div className="cmotForm" ref={containerRef} tabIndex={0} >
         <div className="instructions">
            {parseSanitizedHtml((page > 1 ? reminderHtml : instructionsHtml) || "")}
         </div>
         <table className="tableData center highlightEven no-select">
            <thead>
               <tr>
                  <th className="check columnheader columndata description" >{headers[0]}</th>
                  <th className="name columnheader columndata center choice">{headers[1]}</th>
                  <th className="name columnheader columndata center choice">{headers[2]}</th>
                  <th className="name columnheader columndata center choice">{headers[3]}</th>
                  <th className="name columnheader columndata center choice">{headers[4]}</th>
               </tr>
            </thead>
            <tbody>
               {contentValues.filter((m) => isOnPage(m, page)).map((m, i) => {
                  return (
                     <CMotivator
                        page={page}
                        data={elements}
                        motivator={m}
                        key={i}
                        setValue={onChange}
                        value={values[currentSection.formId + m.id] ?? ""}
                        showValidations={sectionData.showValidations} />
                  );
               })}
            </tbody>
         </table>
      </div>
   );
}

